
import { useCallback } from 'react';
import { CalendarEvent } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { toLocalISOString } from '@/utils/timezoneUtils';

export const useScheduleOperations = (
  teamId: string, 
  user: any, 
  refetch: () => void
) => {
  const handleClearOption = useCallback(async (
    type: 'week' | 'custom' | 'month' | 'missing',
    currentDate: Date,
    customDateRange?: { from: Date; to: Date }
  ) => {
    if (!teamId || !user) return;

    try {
      let startDate: Date;
      let endDate: Date;

      if (type === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else if (type === 'month') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      } else if (type === 'custom' && customDateRange) {
        startDate = customDateRange.from;
        endDate = customDateRange.to;
      } else {
        toast.error('Invalid date range for clearing');
        return;
      }

      if (type === 'missing') {
        // For missing shifts, we need to fetch and filter first
        const { data: schedules, error: fetchError } = await supabase
          .from('team_schedules')
          .select('id, assigned_guards')
          .eq('team_id', teamId)
          .gte('start_date', toLocalISOString(startDate))
          .lte('start_date', toLocalISOString(endDate));

        if (fetchError) throw fetchError;

        const missingShiftIds = schedules?.filter(schedule => 
          !schedule.assigned_guards || schedule.assigned_guards.length === 0
        ).map(schedule => schedule.id) || [];

        if (missingShiftIds.length === 0) {
          toast.success('No missing shifts to clear');
          return;
        }

        const { error } = await supabase
          .from('team_schedules')
          .delete()
          .in('id', missingShiftIds);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('team_schedules')
          .delete()
          .eq('team_id', teamId)
          .gte('start_date', toLocalISOString(startDate))
          .lte('start_date', toLocalISOString(endDate));

        if (error) throw error;
      }

      const typeLabel = type === 'week' ? 'Week' : type === 'month' ? 'Month' : type === 'missing' ? 'Missing Shifts' : 'Custom range';
      toast.success(`${typeLabel} cleared successfully`);
      refetch();
    } catch (error) {
      console.error('Error clearing schedules:', error);
      toast.error('Error clearing schedules');
    }
  }, [teamId, user, refetch]);

  const handleApplyTemplate = useCallback((
    templateShifts: Omit<CalendarEvent, 'assigned_guards' | 'guard_names'>[],
    currentDate: Date
  ) => {
    // The template shifts are already inserted in the database by TemplatesDialog
    // We just need to refresh the calendar to show the new shifts
    console.log('Template applied, refreshing calendar...');
    
    // Add a small delay to ensure database changes are committed
    setTimeout(() => {
      refetch();
    }, 500);
  }, [refetch]);

  return {
    handleClearOption,
    handleApplyTemplate
  };
};
